/**
 * Insights library card (spec §18): one generic renderer over the pure
 * insight builders, grouped picker, CSV and PNG export per view.
 */
import { useMemo, useRef, useState } from 'react';
import {
  ResponsiveContainer, ComposedChart, Area, Line, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from 'recharts';
import type { YearSnapshot } from '../engine/snapshot';
import { INSIGHTS, insightToCsv } from './insights';
import { downloadText } from './csv';
import { fmtCompactUsd, fmtUsd } from './format';

interface Props {
  snapshots: YearSnapshot[];
  todaysDollars: boolean;
}

/** Serialize the rendered SVG chart to a PNG download. */
function exportPng(container: HTMLElement, filename: string): void {
  const svg = container.querySelector('svg.recharts-surface');
  if (!svg) return;
  const clone = svg.cloneNode(true) as SVGSVGElement;
  const { width, height } = svg.getBoundingClientRect();
  clone.setAttribute('width', String(width * 2));
  clone.setAttribute('height', String(height * 2));
  // Inline currentColor-ish CSS vars by resolving computed styles per element.
  const src = svg.querySelectorAll<SVGElement>('*');
  const dst = clone.querySelectorAll<SVGElement>('*');
  src.forEach((el, i) => {
    const cs = getComputedStyle(el);
    const target = dst[i];
    if (!target) return;
    for (const prop of ['fill', 'stroke', 'font-size', 'font-family', 'opacity'] as const) {
      const v = cs.getPropertyValue(prop);
      if (v) target.setAttribute(prop, v);
    }
  });
  const xml = new XMLSerializer().serializeToString(clone);
  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement('canvas');
    canvas.width = width * 2;
    canvas.height = height * 2;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = getComputedStyle(document.body).backgroundColor || '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = filename;
    a.click();
  };
  img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(xml);
}

export function InsightsCard({ snapshots, todaysDollars }: Props) {
  const [key, setKey] = useState(INSIGHTS[0]!.key);
  const chartRef = useRef<HTMLDivElement>(null);

  const def = INSIGHTS.find((i) => i.key === key)!;
  const insight = useMemo(
    () => def.build(snapshots, todaysDollars),
    [def, snapshots, todaysDollars],
  );

  const groups = [...new Set(INSIGHTS.map((i) => i.group))];

  return (
    <section className="card chart-card" aria-label="Insights library">
      <h2 className="section-title">
        Insights
        <span className="note">{insight.note}</span>
      </h2>

      <div className="add-row" style={{ marginBottom: 'var(--space-3)' }}>
        <select value={key} onChange={(e) => setKey(e.target.value)} aria-label="Choose an insight">
          {groups.map((g) => (
            <optgroup key={g} label={g}>
              {INSIGHTS.filter((i) => i.group === g).map((i) => (
                <option key={i.key} value={i.key}>{i.label}</option>
              ))}
            </optgroup>
          ))}
        </select>
        <button className="btn" onClick={() => downloadText(`${def.key}.csv`, insightToCsv(insight))}>
          CSV
        </button>
        <button className="btn" onClick={() => chartRef.current && exportPng(chartRef.current, `${def.key}.png`)}>
          PNG
        </button>
      </div>

      <div ref={chartRef} style={{ width: '100%', height: 280 }}>
        <ResponsiveContainer>
          <ComposedChart data={insight.data} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
            <CartesianGrid stroke="var(--grid)" vertical={false} />
            <XAxis dataKey="year" stroke="var(--axis)" tickLine={false}
              tick={{ fill: 'var(--ink-muted)', fontSize: 12 }} tickMargin={8} minTickGap={28} />
            <YAxis stroke="var(--axis)" tickLine={false} axisLine={false}
              tick={{ fill: 'var(--ink-muted)', fontSize: 12 }}
              tickFormatter={(v: number) => (insight.percent ? `${v}%` : fmtCompactUsd(v))}
              width={insight.percent ? 44 : 64} />
            <Tooltip
              contentStyle={{
                background: 'var(--surface-raised)', border: '1px solid var(--border)',
                borderRadius: 8, fontSize: 13, color: 'var(--ink)',
              }}
              formatter={(value) => (insight.percent ? `${value}%` : fmtUsd(Number(value)))}
              labelStyle={{ color: 'var(--ink-secondary)', fontWeight: 600 }}
            />
            <Legend wrapperStyle={{ fontSize: 13, color: 'var(--ink-secondary)' }} />
            {insight.series.map((s) =>
              insight.kind === 'stacked-area' ? (
                <Area key={s.key} dataKey={s.key} stackId="a" type="monotone"
                  fill={s.color} fillOpacity={0.55} stroke={s.color} strokeWidth={1} />
              ) : insight.kind === 'bar' ? (
                <Bar key={s.key} dataKey={s.key} fill={s.color} radius={[3, 3, 0, 0]} />
              ) : (
                <Line key={s.key} dataKey={s.key} type="monotone"
                  stroke={s.color} strokeWidth={2} dot={false} />
              ),
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}
